import React from 'react';
import styled from 'styled-components';
import palette from '../../lib/styles/palette';
import {useDeviceStatus} from './DeviceStatusProvider';
import SensorIssueGuide from './SensorIssueGuide';
import AppIssueGuide from './AppIssueGuide';

// 기기 행 표기는 관리자 클럽 화면(AdminClubRunningTrackInfo 실시간 데이터)과 같은 색·문구를 쓴다.
//   운동중/정지 = 채운 알약, 회원 = 흰 알약(이름 + 얼굴/전화 + 경과 시간), 앱·센서 = 테두리 알약. 자이로 값은 보이지 않는다.
const Container = styled.section`
    background:#fff; border:1px solid #e0e4ea; border-radius:12px; overflow:hidden; margin-bottom:20px;
    >h2 {padding:18px 24px; margin:0; background:#f8f9fb; font-size:18px;}
    .update {margin:20px 24px; color:#777; font-size:12px;}
    .error {color:#c5222b;}
    .devices {margin:24px; border:1px solid #e0e4ea; border-radius:10px; overflow:hidden;}
    h3 {margin:0; padding:16px 20px; background:#f5f7fa; font-size:15px;}
    .list {padding:14px 20px;}
    .row {display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:12px;
        margin-bottom:8px; padding:12px 16px; border:1px solid #e0e4ea; border-radius:6px; background:#f8f9fb;}
    .left {display:flex; align-items:center; flex-wrap:wrap; gap:8px 12px; flex:1;}
    .name {color:#2c3e50; font-size:1rem; font-weight:700; min-width:80px;}
    .pill {font-size:12px; font-weight:600; padding:4px 12px; border-radius:20px; color:#fff;}
    .exercise {background:#2e7d32;}
    .stop {background:#9e9e9e;}
    .warn {font-size:13px; font-weight:600; color:#e74c3c;}
    .member {display:inline-flex; align-items:center; gap:6px; padding:4px 10px; background:#fff; border:1px solid #e0e4ea; border-radius:20px;}
    .member b {font-size:13px; font-weight:600; color:#2c3e50;}
    .login {font-size:11px; font-weight:500; padding:2px 6px; border-radius:8px;}
    .face {background:#e3f2fd; color:#1976d2;}
    .phone {background:#f3e5f5; color:#7b1fa2;}
    .time {font-size:11px; font-weight:500; padding:2px 6px; border-radius:8px; background:#fff3e0; color:#f57c00; font-family:'Roboto Mono', monospace;}
    .statuses {display:flex; flex-wrap:wrap; align-items:center; gap:10px;}
    .status {font-size:12px; font-weight:600; padding:4px 12px; border-radius:20px; border:1px solid;}
    .normal {border-color:${palette.webCyan[5]}; color:${palette.webCyan[5]}; background:rgba(26,115,233,0.08);}
    .abnormal {border-color:#e74c3c; color:#e74c3c; background:#fff5f5;}
    .unknown {border-color:#adb5bd; color:#868e96; background:#f1f3f5;}
`;

const LOGIN_TYPE = {1:['얼굴', 'face'], 2:['전화', 'phone']};

// 로그인 후 경과 시간 (관리자 화면과 같은 표기)
const elapsed = (startTime) => {
    const sec = Math.max(0, Math.floor(Date.now() / 1000) - startTime);
    const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60;
    return h > 0 ? `${h}시간 ${m}분` : m > 0 ? `${m}분 ${s}초` : `${s}초`;
};

const DeviceRow = ({group, device}) => {
    const loginType = device.loginType || 0;
    const login = LOGIN_TYPE[loginType];
    const exercising = device.sensorActive === true;
    // 러닝은 관리자 화면처럼 항상 운동중/정지를 보인다. 웨이트는 센서가 정상 보고할 때만(종전 그대로).
    const showExercise = group.key === 'running'
        || (group.key === 'weight' && device.sensorStatus === 'normal' && typeof device.sensorActive === 'boolean');
    return <div className="row">
        <div className="left">
            <span className="name">{group.label} {device.number}번</span>
            {showExercise && <span className={'pill ' + (exercising ? 'exercise' : 'stop')}>{exercising ? '운동중' : '정지'}</span>}
            {group.key !== 'tv' && !loginType && exercising && <span className="warn">로그인안함</span>}
            {loginType > 0 && (device.memberName || device.startTime) ? <span className="member">
                {device.memberName && <b>{device.memberName}</b>}
                {login && <span className={'login ' + login[1]}>{login[0]}</span>}
                {device.startTime ? <span className="time">⏱ {elapsed(device.startTime)}</span> : null}
            </span> : null}
        </div>
        <div className="statuses">
            <span className={'status ' + device.appStatus}>{device.appStatus === 'normal' ? '앱정상' : device.appStatus === 'unknown' ? '앱 확인 중' : '앱비정상'}</span>
            {group.key !== 'tv' && ['normal', 'abnormal'].includes(device.sensorStatus) && <span className={'status ' + device.sensorStatus}>
                {device.sensorStatus === 'normal' ? '센서 정상' : '센서 비정상'}
            </span>}
        </div>
    </div>;
};

export default function RunningTrackRealtimeComponent() {
    const {infos, updatedAt, loaded, error, readError} = useDeviceStatus();
    const groups = [
        {key:'tv', title:'TV', label:'TV', devices:infos.filter(d => d.devicetype === 'tv')},
        {key:'running', title:'러닝머신 기기', label:'러닝', devices:infos.filter(d => ['running','kiosk'].includes(d.devicetype))},
        {key:'weight', title:'웨이트 기기', label:'웨이트', devices:infos.filter(d => d.devicetype === 'weight')}
    ];
    return <Container>
        <h2>실시간 기기 현황</h2>
        <div className="update">
            {updatedAt ? '마지막 업데이트: ' + new Date(updatedAt).toLocaleTimeString('ko-KR') + ' · 5초 간격으로 확인' : '기기 상태 확인 중'}
            <div>앱 상태는 앱 연결 여부, 센서 상태는 외부 센서 연결 보고를 기준으로 표시합니다.</div>
            {error && <div className="error" role="alert">{error} 기존 정보는 마지막 조회 결과입니다.</div>}
            {readError && <div className="error" role="alert">{readError}</div>}
        </div>
        {groups.map(group => <React.Fragment key={group.key}>
            <section className="devices">
                <h3>{group.title}</h3>
                <div className="list">
                    {!group.devices.length && <div>{loaded ? '등록된 기기가 없습니다.' : '기기 정보를 확인하고 있습니다.'}</div>}
                    {group.devices.map(device => <DeviceRow key={device.devicetype + ':' + device.number} group={group} device={device}/>)}
                </div>
            </section>
            <AppIssueGuide type={group.key} devices={group.devices}/>
            {group.key !== 'tv' && <SensorIssueGuide type={group.key} devices={group.devices}/>}
        </React.Fragment>)}
    </Container>;
}
